// src/modules/app-views/welcome/pages/Init/modals/PendingInvitationsModal/InvitationCard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback } from 'react';
import { Button, Card, Divider, Flex, Tag, theme } from 'antd';
import { CheckOutlined, CloseOutlined, UndoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import translateX from 'utils/translateX';
import AccountProfileTitle from 'modules/app-views/account/components/AccountProfileTitle';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const InvitationCard = ({ invitation, decision, onDecision, disabled }) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { token } = theme.useToken();

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const inviter = findInviter(invitation);
  const logo =
    invitation?.avatar?.base64Thumb ||
    invitation?.avatar?.thumbUrl ||
    invitation?.avatar?.largeUrl;

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleAccept = useCallback(() => {
    onDecision?.(invitation._id, 'accepted');
  }, [invitation._id, onDecision]);

  const handleDecline = useCallback(() => {
    onDecision?.(invitation._id, 'declined');
  }, [invitation._id, onDecision]);

  const handleReset = useCallback(() => {
    onDecision?.(invitation._id, null);
  }, [invitation._id, onDecision]);

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------
  const borderColor =
    decision === 'accepted'
      ? token.colorSuccess
      : decision === 'declined'
        ? token.colorError
        : token.colorBorderSecondary;

  const bgColor =
    decision === 'accepted'
      ? token.colorSuccessBg
      : decision === 'declined'
        ? token.colorErrorBg
        : token.colorBgContainer;

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <Card
      style={{
        border: `1px solid ${borderColor}`,
        backgroundColor: bgColor,
        transition: 'all 0.2s ease',
      }}
      styles={{ body: { padding: '16px' } }}
    >
      {/* Header: Avatar + Organization + Profile */}
      <Flex gap={12} align="center">
        <SimpleAvatar
          src={logo}
          name={invitation?.organization}
          size={44}
          shape="square"
          backColor={invitation?.backColor}
          foreColor={invitation?.foreColor}
          showBorder={false}
        />

        <Flex vertical style={{ flex: 1 }}>
          <h4 className="mb-0" style={{ lineHeight: '1.3', color: token.colorText }}>
            {invitation?.organization}
          </h4>
          <AccountProfileTitle profile={invitation?.profile} />
        </Flex>
      </Flex>

      <Divider style={{ margin: '12px 0', borderColor: token.colorBorderSecondary }} />

      {/* Inviter info */}
      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: token.colorTextSecondary }}>
          {translateX('invited_by')}: <strong>{inviter}</strong>
        </span>
        {invitation?.invitedAt && (
          <span style={{ fontSize: 12, color: token.colorTextTertiary }}>
            {dayjs(invitation.invitedAt).format('DD/MM/YYYY')}
          </span>
        )}
      </Flex>

      {/* Actions or Decision Tag */}
      {!decision ? (
        <Flex gap={8}>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={handleAccept}
            disabled={disabled}
            block
          >
            {translateX('accept_invitation')}
          </Button>
          <Button icon={<CloseOutlined />} onClick={handleDecline} disabled={disabled} danger block>
            {translateX('decline_invitation')}
          </Button>
        </Flex>
      ) : (
        <Flex justify="space-between" align="center">
          <Tag
            color={decision === 'accepted' ? 'success' : 'error'}
            variant="solid"
            style={{ fontSize: 13 }}
          >
            {translateX(
              decision === 'accepted' ? 'invitation_accepted' : 'invitation_declined'
            )}
          </Tag>
          <Button
            type="link"
            size="small"
            icon={<UndoOutlined />}
            onClick={handleReset}
            disabled={disabled}
          >
            {translateX('change_decision')}
          </Button>
        </Flex>
      )}
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default InvitationCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function findInviter(invitation) {
  // Find the team member who invited this user
  const invitedBy = invitation?.invitedByAccountId;
  if (!invitedBy || !invitation?.team?.length) return '—';

  const member = invitation.team.find(m => m._id === invitedBy);
  return member?.name || '—';
}
